import React from "react";
import { motion } from "framer-motion";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { Camera, MapPin, Clock, Loader2, AlertCircle, User, Truck, ExternalLink, RefreshCw } from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function fmtDuration(start, end) {
  if (!start) return "—";
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const m = Math.max(0, Math.round((e - s) / 60000));
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
}

/**
 * Admin Live Jobs — shows in-progress jobs with clock-in photo, GPS, and a
 * live elapsed counter. Built for catching/auditing data discrepancies.
 */
export default function AdminLiveJobsPanel() {
  const [jobs, setJobs] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [selected, setSelected] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await apiClient.get("/admin/jobs/live");
      setJobs(data.jobs || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load live jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
    // Refresh every 30s while the panel is mounted so admin sees live updates
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  return (
    <section className="mb-16" data-testid="admin-live-jobs-section">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-[#0A0A0A]">Live jobs</h2>
          <p className="mt-1 text-xs text-[#475569]">Drivers currently clocked-in. Auto-refresh every 30s.</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="admin-live-jobs-refresh"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E4D55] hover:text-[#163C42] disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3">
          <AlertCircle size={16} className="mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {!error && !loading && jobs.length === 0 && (
        <div className="bg-white border border-[#0A0A0A]/10 p-8 text-center" data-testid="admin-live-jobs-empty">
          <Clock size={28} className="mx-auto text-[#475569] mb-3" />
          <p className="font-display text-lg font-bold tracking-tight text-[#0A0A0A]">No live jobs right now</p>
          <p className="mt-1 text-sm text-[#475569]">Drivers haven&apos;t clocked in to any active jobs yet.</p>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="admin-live-jobs-grid">
          {jobs.map((j) => (
            <motion.div
              key={j.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid={`admin-live-job-${j.id}`}
              className="bg-white border border-[#0A0A0A]/10 overflow-hidden"
            >
              {/* Clock-in photo as the hero */}
              {j.clock_in_photo_url ? (
                <button
                  onClick={() => setSelected(j)}
                  className="block w-full bg-[#F5F5F5] aspect-video relative group"
                  data-testid={`admin-live-job-photo-${j.id}`}
                >
                  <img src={j.clock_in_photo_url} alt="clock-in proof" className="w-full h-full object-cover" />
                  <span className="absolute top-2 left-2 bg-[#5BB247] text-white text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-1 inline-flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                    {j.status === "in_progress" ? "Live" : "Awaiting confirm"}
                  </span>
                  <span className="absolute top-2 right-2 bg-black/70 text-white text-[11px] px-2 py-1">
                    Clock-in · {fmtTime(j.clock_in_at)}
                  </span>
                </button>
              ) : (
                <div className="bg-[#F5F5F5] aspect-video flex items-center justify-center">
                  <Camera size={32} className="text-[#475569]" />
                </div>
              )}

              <div className="p-4">
                <p className="font-display text-base font-bold tracking-tight text-[#0A0A0A] line-clamp-1">{j.title}</p>
                {j.geofence && !j.geofence.within_geofence && (
                  <div data-testid={`geofence-warning-${j.id}`} className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-bold text-[#A66800] bg-[#F5A623]/10 border border-[#F5A623]/40 px-2 py-1">
                    ⚠ Clocked in <strong>{j.geofence.distance_from_pickup_m}m</strong> from pickup (limit {j.geofence.radius_m}m)
                  </div>
                )}
                <p className="mt-1 text-xs text-[#475569] line-clamp-1">
                  <MapPin size={11} className="inline mr-1 -mt-0.5" />
                  {j.pickup_address} → {j.dropoff_address}
                </p>

                <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Driver</p>
                    <p className="mt-0.5 font-semibold text-[#0A0A0A] line-clamp-1">{j.driver?.name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Elapsed</p>
                    <p className="mt-0.5 font-semibold text-[#0A0A0A]">
                      {fmtDuration(j.clock_in_at, j.clock_out_at)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#475569]">Booked</p>
                    <p className="mt-0.5 font-semibold text-[#0A0A0A]">{j.estimated_hours ? `${j.estimated_hours}h` : "—"}</p>
                  </div>
                </div>

                {(j.clock_in_lat && j.clock_in_lng) && (
                  <a
                    href={`https://www.google.com/maps?q=${j.clock_in_lat},${j.clock_in_lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`admin-live-job-map-${j.id}`}
                    className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-[#1E4D55] hover:underline"
                  >
                    <MapPin size={11} /> Open clock-in pin · ±{Math.round(j.clock_in_accuracy_m || 0)}m
                    <ExternalLink size={9} />
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Photo lightbox */}
      {selected && (
        <div
          data-testid="admin-photo-lightbox"
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-4xl w-full bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 flex items-start justify-between gap-4 border-b border-[#0A0A0A]/10">
              <div>
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55]">Clock-in proof</p>
                <p className="mt-1 font-display text-lg font-bold tracking-tight text-[#0A0A0A]">{selected.title}</p>
                <p className="mt-1 text-xs text-[#475569]">
                  <User size={11} className="inline mr-1 -mt-0.5" /> {selected.driver?.name} · <Clock size={11} className="inline mr-1 -mt-0.5" /> {fmtTime(selected.clock_in_at)}
                </p>
              </div>
              <button onClick={() => setSelected(null)} className="text-sm text-[#475569] hover:text-[#0A0A0A] px-3 py-1">Close</button>
            </div>
            <img src={selected.clock_in_photo_url} alt="clock-in proof full" className="w-full max-h-[70vh] object-contain bg-black" />
            {selected.clock_out_photo_url && (
              <div className="p-4 border-t border-[#0A0A0A]/10">
                <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1E4D55] mb-2">Clock-out proof · {fmtTime(selected.clock_out_at)}</p>
                <img src={selected.clock_out_photo_url} alt="clock-out proof" className="w-full max-h-[40vh] object-contain bg-black" />
              </div>
            )}
          </motion.div>
        </div>
      )}
    </section>
  );
}
